import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';

const BRAND = '#682622';

/* Row widths vary per row so the skeleton looks natural */
const ROW_WIDTHS = [
  [88, 68, 52, 78, 58, 62, 48, 55],
  [72, 80, 44, 65, 70, 50, 58, 48],
  [92, 60, 56, 82, 52, 68, 44, 60],
  [78, 72, 48, 70, 64, 55, 52, 50],
  [85, 65, 44, 75, 56, 60, 46, 58],
  [70, 78, 52, 68, 62, 52, 56, 44],
  [90, 62, 50, 80, 58, 65, 48, 62],
  [76, 70, 46, 72, 66, 58, 50, 52],
  [84, 66, 54, 76, 54, 62, 44, 56],
  [80, 74, 48, 74, 60, 56, 52, 48],
  [88, 64, 42, 70, 68, 60, 46, 60],
  [74, 76, 50, 78, 56, 54, 54, 50],
];

const useStyles = makeStyles(() => ({
  wrap: {
    borderRadius: 12,
    border: '1px solid #e8ecef',
    boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    backgroundColor: '#fff',
    marginBottom: 8,
  },
  toolbar: {
    height: 56,
    backgroundColor: BRAND,
    display: 'flex',
    alignItems: 'center',
    padding: '0 16px',
    gap: 12,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    padding: '10px 12px',
    backgroundColor: '#fafafa',
    borderBottom: '2px solid #e8ecef',
  },
  dataRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    padding: '9px 12px',
    borderBottom: '1px solid #f5f5f5',
    '&:last-child': { borderBottom: 'none' },
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 16,
    padding: '10px 16px',
    borderTop: '1px solid #e8ecef',
    backgroundColor: '#fafafa',
  },
  bone: {
    backgroundColor: '#eeeeee',
    borderRadius: 4,
    position: 'relative',
    overflow: 'hidden',
    flexShrink: 0,
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.65) 50%, transparent 100%)',
      animation: '$shimmer 1.5s infinite',
    },
  },
  '@keyframes shimmer': {
    '0%':   { transform: 'translateX(-100%)' },
    '100%': { transform: 'translateX(100%)' },
  },
}));

const DataTableSkeleton = ({ rows = 12 }) => {
  const classes = useStyles();
  const visibleRows = ROW_WIDTHS.slice(0, Math.min(rows, ROW_WIDTHS.length));

  return (
    <div className={classes.wrap}>

      {/* Toolbar */}
      <div className={classes.toolbar}>
        <div className={classes.bone} style={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)' }} />
        <div className={classes.bone} style={{ width: 150, height: 14, backgroundColor: 'rgba(255,255,255,0.2)' }} />
        <div style={{ flex: 1 }} />
        {[22, 22, 22, 22].map((s, i) => (
          <div key={i} className={classes.bone} style={{ width: s, height: s, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.15)' }} />
        ))}
      </div>

      {/* Column headers */}
      <div className={classes.headerRow}>
        {[55, 65, 50, 75, 55, 65, 50, 60].map((w, i) => (
          <div key={i} className={classes.bone} style={{ width: w, height: 8, borderRadius: 3 }} />
        ))}
      </div>

      {/* Data rows */}
      {visibleRows.map((widths, i) => (
        <div key={i} className={classes.dataRow}>
          {widths.map((w, j) => (
            <div key={j} className={classes.bone} style={{ width: w, height: 10 }} />
          ))}
        </div>
      ))}

      {/* Pagination */}
      <div className={classes.pagination}>
        <div className={classes.bone} style={{ width: 130, height: 10 }} />
        <div className={classes.bone} style={{ width: 80, height: 10 }} />
        <div className={classes.bone} style={{ width: 60, height: 24, borderRadius: 4 }} />
      </div>

    </div>
  );
};

DataTableSkeleton.propTypes = { rows: PropTypes.number };
export default DataTableSkeleton;
